<template>
  <div id="furnace-assembly-page-container" class="operator-page">
    <!-- Top Tab Bar -->
    <div class="assembly-top-header">
      <div class="tabs-bar">
        <div class="tab-item active">
          <span>{{ isEditing ? 'Edição de carga' : 'Montagem de carga' }} — {{ furnaceName }}</span>
        </div>
      </div>
    </div>

    <!-- Sub-Header Info Bar -->
    <div class="assembly-sub-header d-flex align-items-center justify-content-between">
      <!-- Back Button -->
      <div class="sub-header-left">
        <button class="btn-back-link" @click="goBack">
          <feather-icon icon="ChevronLeftIcon" size="18" class="mr-25" />
          <span>Fornos</span>
        </button>
      </div>

      <!-- Center Charge Code & Status -->
      <div class="sub-header-center text-center">
        <h3 class="charge-code-title mb-0">{{ chargeCodeDisplay }}</h3>
        <span class="assembly-status-text" :class="{ 'status-editing-active': isEditing }">
          {{ isEditing ? 'Edição da fornada ativa' : 'Montagem em andamento' }}
        </span>
      </div>

      <!-- Right Cestos Counter -->
      <div class="sub-header-right text-right">
        <div class="cestos-counter-box">
          <span class="cestos-label">CESTOS</span>
          <span class="cestos-value">{{ selectedGroups.length }} / {{ furnaceCapacity }}</span>
        </div>
      </div>
    </div>

    <!-- Main Body Area -->
    <div class="operator-body">
      <!-- Loading State -->
      <div v-if="loadingBaskets" class="text-center py-5">
        <b-spinner variant="warning" label="Carregando..." class="mb-1" />
        <p class="text-muted">Carregando cestos disponíveis do corte...</p>
      </div>

      <div v-else class="assembly-panels-wrapper">
        <!-- Painel Esquerdo: CESTOS DISPONÍVEIS -->
        <div class="assembly-panel-box left-panel">
          <div class="panel-header-row d-flex align-items-center justify-content-between mb-1">
            <span class="panel-box-title">CESTOS DISPONÍVEIS</span>
            <span class="panel-box-counter">
              <template v-if="hasActiveFilters">
                {{ availableUnselectedGroups.length }} encontrado(s) (de {{ totalAvailableUnselectedCount }})
              </template>
              <template v-else>
                {{ availableUnselectedGroups.length }} de {{ availableGroups.length }}
              </template>
            </span>
          </div>

          <!-- Filtros Isolados: Cesto, OP e Perfil -->
          <div class="search-box-panel mb-1">
            <div class="filters-row">
              <!-- Filtro 1: Cesto -->
              <div class="search-input-wrapper filter-field filter-cesto">
                <feather-icon icon="BoxIcon" size="14" class="search-icon" />
                <input
                  type="text"
                  v-model="filterBasket"
                  placeholder="Cesto..."
                  class="search-input-field"
                  title="Filtrar por código do cesto (ex: C01)"
                />
                <button
                  v-if="filterBasket"
                  type="button"
                  class="clear-search-btn"
                  title="Limpar filtro de cesto"
                  @click="filterBasket = ''"
                >
                  <feather-icon icon="XIcon" size="13" />
                </button>
              </div>

              <!-- Filtro 2: OP -->
              <div class="search-input-wrapper filter-field filter-op">
                <feather-icon icon="FileTextIcon" size="14" class="search-icon" />
                <input
                  type="text"
                  v-model="filterOp"
                  placeholder="OP..."
                  class="search-input-field"
                  title="Filtrar por número da OP"
                />
                <button
                  v-if="filterOp"
                  type="button"
                  class="clear-search-btn"
                  title="Limpar filtro de OP"
                  @click="filterOp = ''"
                >
                  <feather-icon icon="XIcon" size="13" />
                </button>
              </div>

              <!-- Filtro 3: Perfil -->
              <div class="search-input-wrapper filter-field filter-perfil">
                <feather-icon icon="LayersIcon" size="14" class="search-icon" />
                <input
                  type="text"
                  v-model="filterProfile"
                  placeholder="Perfil / Material..."
                  class="search-input-field"
                  title="Filtrar por perfil ou material"
                />
                <button
                  v-if="filterProfile"
                  type="button"
                  class="clear-search-btn"
                  title="Limpar filtro de perfil"
                  @click="filterProfile = ''"
                >
                  <feather-icon icon="XIcon" size="13" />
                </button>
              </div>

              <!-- Botão Limpar Tudo -->
              <button
                v-if="hasActiveFilters"
                type="button"
                class="btn-clear-all-filters"
                title="Limpar todos os filtros"
                @click="clearAllFilters"
              >
                <feather-icon icon="RotateCcwIcon" size="13" class="mr-25" />
                <span>Limpar</span>
              </button>
            </div>
          </div>

          <!-- Tabela de Cestos Disponíveis -->
          <div class="panel-table-container">
            <div class="panel-table-head table-head-navy">
              <div class="col-id">Cesto</div>
              <div class="col-details col-details-head">
                <div class="cell-op">OP</div>
                <div class="cell-mat">Material / Perfil</div>
                <div class="cell-temper text-center">Têmpera</div>
                <div class="cell-qty text-center">Qtd. (un)</div>
                <div class="cell-size text-center">Tam. (mm)</div>
              </div>
              <div
                class="col-action text-center"
                :title="isAllFilteredStaged ? 'Desmarcar todos os cestos visíveis' : 'Marcar todos os cestos visíveis'"
              >
                <div
                  v-if="availableUnselectedGroups.length > 0"
                  class="custom-checkbox-box cursor-pointer"
                  :class="{ 'is-checked': isAllFilteredStaged }"
                  @click.stop="toggleSelectAllFiltered"
                >
                  <feather-icon v-if="isAllFilteredStaged" icon="CheckIcon" size="14" />
                </div>
              </div>
            </div>

            <div class="panel-table-body custom-scrollbar">
              <div v-if="availableUnselectedGroups.length === 0" class="empty-table-state text-center py-4">
                <feather-icon :icon="hasActiveFilters ? 'SearchIcon' : 'InboxIcon'" size="32" class="text-muted mb-1" />
                <p class="text-muted mb-0 font-weight-500">
                  {{ hasActiveFilters ? 'Nenhum cesto encontrado para os filtros aplicados.' : 'Nenhum cesto disponível para seleção.' }}
                </p>
                <small class="text-muted">
                  {{ hasActiveFilters ? 'Tente ajustar ou limpar os filtros de Cesto, OP ou Perfil.' : 'Todos os cestos já foram adicionados ou não há novos cestos no corte.' }}
                </small>
              </div>

              <div
                v-for="group in availableUnselectedGroups"
                :key="group.key"
                class="panel-table-row"
                :class="{ 'row-staged': isGroupStaged(group.key) }"
                @click="toggleStageGroup(group)"
              >
                <div class="col-id font-weight-bold">
                  <div class="basket-code font-weight-bold">{{ group.basketCode }}</div>
                  <span v-if="group.items.length > 1" class="badge badge-light-primary multi-op-badge" title="Cesto com mais de uma OP">
                    {{ group.items.length }} OPs
                  </span>
                </div>
                <div class="col-details">
                  <div
                    v-for="(item, itemIdx) in group.items"
                    :key="item.id"
                    class="details-line"
                    :class="{ 'border-top-dashed': itemIdx > 0 }"
                  >
                    <div class="cell-op font-weight-bold text-dark">{{ item.orderNumber ? item.orderNumber : '-' }}</div>
                    <div class="cell-mat text-dark font-weight-600" :title="item.materialName">
                      {{ item.materialName || 'Material Padrão' }}
                    </div>
                    <div class="cell-temper text-center">
                      <span class="badge badge-light-secondary font-weight-600" title="Têmpera">{{ item.temper || '-' }}</span>
                    </div>
                    <div class="cell-qty text-center">
                      <span class="qty-val">{{ formatNumber(item.barQuantity) }}</span>
                      <span class="unit-text">un</span>
                    </div>
                    <div class="cell-size text-center">
                      <template v-if="formatBarSizeNumber(item) !== '-'">
                        <span class="size-val">{{ formatBarSizeNumber(item) }}</span>
                        <span class="unit-text">mm</span>
                      </template>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </div>
                  <div v-if="group.items.length > 1" class="multi-op-total-row">
                    <div class="cell-op"></div>
                    <div class="cell-mat text-right">
                      <span class="total-label">Total cesto:</span>
                    </div>
                    <div class="cell-temper"></div>
                    <div class="cell-qty text-center">
                      <span class="total-qty">{{ formatNumber(group.totalBarQuantity) }}</span>
                      <span class="unit-text">un</span>
                    </div>
                    <div class="cell-size"></div>
                  </div>
                </div>
                <div class="col-action" @click.stop="toggleStageGroup(group)">
                  <div class="custom-checkbox-box" :class="{ 'is-checked': isGroupStaged(group.key) }">
                    <feather-icon v-if="isGroupStaged(group.key)" icon="CheckIcon" size="14" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Botão Central de Transferência -->
        <div class="assembly-transfer-column d-flex flex-column align-items-center justify-content-center">
          <span class="transfer-label mb-50">Adicionar à fornada</span>
          <button
            class="btn-transfer-circle"
            :disabled="stagedGroupKeys.length === 0 || selectedGroups.length >= furnaceCapacity"
            @click="addStagedToSelected"
            :title="selectedGroups.length >= furnaceCapacity ? `Capacidade máxima de ${furnaceCapacity} atingida` : 'Adicionar cestos marcados à carga'"
          >
            <feather-icon icon="ChevronRightIcon" size="22" />
          </button>
        </div>

        <!-- Painel Direito: CESTOS SELECIONADOS -->
        <div class="assembly-panel-box right-panel">
          <div class="panel-header-row d-flex align-items-center justify-content-between mb-1">
            <span class="panel-box-title">CESTOS SELECIONADOS</span>
            <span class="panel-box-counter">{{ formatTwoDigits(selectedGroups.length) }} de {{ formatTwoDigits(furnaceCapacity) }}</span>
          </div>

          <!-- Tabela de Cestos Selecionados -->
          <div class="panel-table-container">
            <div class="panel-table-head table-head-orange">
              <div class="col-id">Cesto</div>
              <div class="col-details col-details-head">
                <div class="cell-op">OP</div>
                <div class="cell-mat">Material</div>
                <div class="cell-temper text-center">Têmpera</div>
                <div class="cell-qty text-center">Qtd. (un)</div>
                <div class="cell-size text-center">Tam. (mm)</div>
              </div>
              <div class="col-action"></div>
            </div>

            <div class="panel-table-body custom-scrollbar">
              <div v-if="selectedGroups.length === 0" class="empty-table-state text-center py-4">
                <feather-icon icon="LayersIcon" size="32" class="text-muted mb-1" />
                <p class="text-muted mb-0 font-weight-500">Nenhum cesto selecionado.</p>
                <small class="text-muted">Marque os cestos à esquerda e clique na seta para montar a fornada.</small>
              </div>

              <div
                v-for="group in selectedGroups"
                :key="group.key"
                class="panel-table-row"
              >
                <div class="col-id font-weight-bold">
                  <div class="basket-code font-weight-bold">{{ group.basketCode }}</div>
                  <span v-if="group.items.length > 1" class="badge badge-light-primary multi-op-badge" title="Cesto com mais de uma OP">
                    {{ group.items.length }} OPs
                  </span>
                </div>
                <div class="col-details">
                  <div
                    v-for="(item, itemIdx) in group.items"
                    :key="item.id"
                    class="details-line"
                    :class="{ 'border-top-dashed': itemIdx > 0 }"
                  >
                    <div class="cell-op font-weight-bold text-dark">{{ item.orderNumber ? item.orderNumber : '-' }}</div>
                    <div class="cell-mat text-dark font-weight-600" :title="item.materialName">
                      {{ item.materialName || 'Material Padrão' }}
                    </div>
                    <div class="cell-temper text-center">
                      <span class="badge badge-light-secondary font-weight-600" title="Têmpera">{{ item.temper || '-' }}</span>
                    </div>
                    <div class="cell-qty text-center">
                      <span class="qty-val">{{ formatNumber(item.barQuantity) }}</span>
                      <span class="unit-text">un</span>
                    </div>
                    <div class="cell-size text-center">
                      <template v-if="formatBarSizeNumber(item) !== '-'">
                        <span class="size-val">{{ formatBarSizeNumber(item) }}</span>
                        <span class="unit-text">mm</span>
                      </template>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </div>
                  <div v-if="group.items.length > 1" class="multi-op-total-row">
                    <div class="cell-op"></div>
                    <div class="cell-mat text-right">
                      <span class="total-label">Total cesto:</span>
                    </div>
                    <div class="cell-temper"></div>
                    <div class="cell-qty text-center">
                      <span class="total-qty">{{ formatNumber(group.totalBarQuantity) }}</span>
                      <span class="unit-text">un</span>
                    </div>
                    <div class="cell-size"></div>
                  </div>
                </div>
                <div class="col-action">
                  <button
                    class="btn-remove-basket"
                    @click="removeSelectedGroup(group.key)"
                    title="Remover da carga"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rodapé: Botão de Ação Principal -->
      <div class="assembly-footer-action mt-3">
        <button
          class="btn-confirm-appointment"
          :class="{ 'btn-save-edit': isEditing }"
          :disabled="saving || (!isEditing && selectedGroups.length === 0)"
          @click="confirmAppointment"
        >
          <b-spinner v-if="saving" small class="mr-50" />
          <feather-icon v-else :icon="isEditing ? 'SaveIcon' : 'CheckIcon'" size="18" class="mr-50" />
          <span>{{ isEditing ? (selectedGroups.length === 0 ? 'Salvar e Liberar Forno' : 'Salvar Alterações da Carga') : 'Realizar Apontamento' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { BSpinner } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { getAvailableBaskets, getFurnaces, createFurnaceCharge, updateFurnaceChargeBaskets } from './extrusionApi';

export default {
  name: 'FurnaceChargeAssembly',
  components: {
    BSpinner,
    ToastificationContent
  },
  data() {
    return {
      rawAvailableBaskets: [],
      stagedGroupKeys: [],
      selectedGroups: [],
      furnaceCapacity: 12,
      dynamicFurnaceName: '',
      existingCharge: null,
      loadingBaskets: false,
      saving: false,
      filterBasket: '',
      filterOp: '',
      filterProfile: '',
      letters: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
    };
  },
  computed: {
    furnaceId() {
      return parseInt(this.$route.params.furnaceId || 1, 10);
    },
    furnaceName() {
      return this.dynamicFurnaceName || `Forno ${this.furnaceId}`;
    },
    currentSiteId() {
      const cookieSite = this.$cookies?.get('userInfo')?.currentSite;
      if (cookieSite) {
        if (typeof cookieSite === 'object' && cookieSite.id) return cookieSite.id;
        if (typeof cookieSite === 'number') return cookieSite;
        if (!isNaN(cookieSite)) return parseInt(cookieSite, 10);
      }
      const storedSite = localStorage.getItem('currentSite') || localStorage.getItem('siteId');
      if (storedSite) {
        try {
          const parsed = JSON.parse(storedSite);
          if (parsed && parsed.id) return parsed.id;
          if (typeof parsed === 'number') return parsed;
        } catch (e) {
          if (!isNaN(storedSite)) return parseInt(storedSite, 10);
        }
      }
      const authUser = this.$store ? this.$store.state?.auth?.user : null;
      if (authUser && authUser.siteId) return authUser.siteId;
      return 1;
    },
    isEditing() {
      return !!this.existingCharge;
    },
    previewChargeCode() {
      const yr = new Date().getFullYear();
      return `FRN-${yr}-00${this.furnaceId}`;
    },
    chargeCodeDisplay() {
      return (this.existingCharge && this.existingCharge.chargeCode) || this.previewChargeCode;
    },
    availableGroups() {
      return this.groupBaskets(this.rawAvailableBaskets);
    },
    totalAvailableUnselectedCount() {
      const selectedKeys = new Set(this.selectedGroups.map(g => g.key));
      return this.availableGroups.filter(g => !selectedKeys.has(g.key)).length;
    },
    hasActiveFilters() {
      return !!(
        (this.filterBasket && this.filterBasket.trim()) ||
        (this.filterOp && this.filterOp.trim()) ||
        (this.filterProfile && this.filterProfile.trim())
      );
    },
    availableUnselectedGroups() {
      const selectedKeys = new Set(this.selectedGroups.map(g => g.key));
      const unselected = this.availableGroups.filter(g => !selectedKeys.has(g.key));

      if (!this.hasActiveFilters) {
        return unselected;
      }

      const basketQ = (this.filterBasket || '').trim().toLowerCase();
      const opQ = (this.filterOp || '').trim().toLowerCase();
      const profileQ = (this.filterProfile || '').trim().toLowerCase();

      return unselected.filter(group => {
        // 1. Filtro de Cesto (se informado)
        if (basketQ) {
          const matchCesto =
            (group.basketCode && group.basketCode.toLowerCase().includes(basketQ)) ||
            (group.key && group.key.toLowerCase().includes(basketQ));
          if (!matchCesto) return false;
        }

        // 2. Filtro de OP (se informado)
        if (opQ) {
          const matchOp =
            Array.isArray(group.items) &&
            group.items.some(item => item.orderNumber && String(item.orderNumber).toLowerCase().includes(opQ));
          if (!matchOp) return false;
        }

        // 3. Filtro de Perfil / Material (se informado)
        if (profileQ) {
          const matchProfile =
            Array.isArray(group.items) &&
            group.items.some(item => {
              const matMatch = item.materialName && item.materialName.toLowerCase().includes(profileQ);
              const prodMatch = item.product && item.product.toLowerCase().includes(profileQ);
              const codeMatch = item.productCode && String(item.productCode).toLowerCase().includes(profileQ);
              return matMatch || prodMatch || codeMatch;
            });
          if (!matchProfile) return false;
        }

        return true;
      });
    },
    isAllFilteredStaged() {
      if (this.availableUnselectedGroups.length === 0) return false;
      return this.availableUnselectedGroups.every(g => this.isGroupStaged(g.key));
    }
  },
  mounted() {
    this.fetchBaskets();
    this.fetchFurnaceConfig();
  },
  methods: {
    clearAllFilters() {
      this.filterBasket = '';
      this.filterOp = '';
      this.filterProfile = '';
    },
    groupBaskets(rawList) {
      if (!Array.isArray(rawList)) return [];
      const map = new Map();
      for (const b of rawList) {
        const rawCode = b.basketCode ? String(b.basketCode).trim() : '';
        const key = rawCode ? rawCode.toUpperCase() : `ID_${b.id}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            basketCode: rawCode || (b.id ? `C${b.id}` : '-'),
            items: [],
            totalBarQuantity: 0,
            createdAt: b.createdAt
          });
        }
        const group = map.get(key);
        group.items.push(b);
        group.totalBarQuantity += Number(b.barQuantity || 0);
      }
      return Array.from(map.values());
    },
    async fetchFurnaceConfig() {
      try {
        const res = await getFurnaces(this.currentSiteId);
        const list = res.data && (Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []));
        const found = list.find(f => f.furnaceId === this.furnaceId);
        if (found) {
          this.furnaceCapacity = found.capacity || 12;
          if (found.furnaceName) this.dynamicFurnaceName = found.furnaceName;
          if (found.currentCharge && found.status !== 'Disponível') {
            this.existingCharge = found.currentCharge;
            if (Array.isArray(found.currentCharge.baskets) && found.currentCharge.baskets.length > 0) {
              this.selectedGroups = this.groupBaskets(found.currentCharge.baskets);
            }
          }
        }
      } catch (err) {
        console.error('Erro ao buscar configuração do forno:', err);
      }
    },
    async fetchBaskets() {
      this.loadingBaskets = true;
      try {
        const res = await getAvailableBaskets(this.currentSiteId);
        const list = res.data && (Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []));
        this.rawAvailableBaskets = list;
      } catch (err) {
        console.error('Erro ao carregar cestos disponíveis:', err);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            icon: 'AlertTriangleIcon',
            text: 'Erro ao buscar cestos disponíveis.',
            variant: 'danger'
          }
        });
      } finally {
        this.loadingBaskets = false;
      }
    },
    getLetterId(index, basket) {
      if (basket && basket.basketCode && String(basket.basketCode).trim().length > 0) {
        return String(basket.basketCode).trim();
      }
      return `C${index + 1}`;
    },
    formatTwoDigits(num) {
      return String(num).padStart(2, '0');
    },
    isGroupStaged(groupKey) {
      return this.stagedGroupKeys.includes(groupKey);
    },
    toggleStageGroup(group) {
      const idx = this.stagedGroupKeys.indexOf(group.key);
      if (idx > -1) {
        this.stagedGroupKeys.splice(idx, 1);
      } else {
        if (this.selectedGroups.length + this.stagedGroupKeys.length >= this.furnaceCapacity) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Limite de Carga',
              icon: 'AlertCircleIcon',
              text: `A capacidade máxima da carga deste forno é de ${this.furnaceCapacity} cestos.`,
              variant: 'warning'
            }
          });
          return;
        }
        this.stagedGroupKeys.push(group.key);
      }
    },
    toggleSelectAllFiltered() {
      if (this.isAllFilteredStaged) {
        const visibleKeys = new Set(this.availableUnselectedGroups.map(g => g.key));
        this.stagedGroupKeys = this.stagedGroupKeys.filter(k => !visibleKeys.has(k));
      } else {
        const currentSelectedCount = this.selectedGroups.length;
        const availableCapacity = this.furnaceCapacity - currentSelectedCount;
        if (availableCapacity <= 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Limite de Carga',
              icon: 'AlertCircleIcon',
              text: `A capacidade máxima da carga deste forno é de ${this.furnaceCapacity} cestos.`,
              variant: 'warning'
            }
          });
          return;
        }

        const newStaged = new Set(this.stagedGroupKeys);
        let reachedLimit = false;
        for (const g of this.availableUnselectedGroups) {
          if (!newStaged.has(g.key)) {
            if (newStaged.size >= availableCapacity) {
              reachedLimit = true;
              break;
            }
            newStaged.add(g.key);
          }
        }
        this.stagedGroupKeys = Array.from(newStaged);

        if (reachedLimit) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Capacidade Restante Atingida',
              icon: 'AlertCircleIcon',
              text: `Foram marcados cestos até a capacidade disponível do forno (${availableCapacity} cestos).`,
              variant: 'warning'
            }
          });
        }
      }
    },
    addStagedToSelected() {
      const stagedSet = new Set(this.stagedGroupKeys);
      const toAdd = this.availableGroups.filter(g => stagedSet.has(g.key));
      
      const availableCapacity = this.furnaceCapacity - this.selectedGroups.length;
      const finalToAdd = toAdd.slice(0, availableCapacity);

      this.selectedGroups.push(...finalToAdd);
      this.stagedGroupKeys = [];
    },
    removeSelectedGroup(groupKey) {
      const removed = this.selectedGroups.find(g => g.key === groupKey);
      this.selectedGroups = this.selectedGroups.filter(g => g.key !== groupKey);
      if (removed) {
        for (const item of removed.items) {
          const exists = this.rawAvailableBaskets.some(b => b.id === item.id);
          if (!exists) {
            this.rawAvailableBaskets.push(item);
          }
        }
      }
    },
    async confirmAppointment() {
      if (!this.isEditing && this.selectedGroups.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atenção',
            icon: 'AlertCircleIcon',
            text: 'Selecione ao menos 1 cesto para formar a fornada.',
            variant: 'warning'
          }
        });
        return;
      }

      this.saving = true;
      try {
        const allBasketIds = this.selectedGroups.flatMap(g => g.items.map(item => item.id));

        if (this.isEditing) {
          const payload = {
            chargeId: this.existingCharge.chargeId || this.existingCharge.id,
            basketIds: allBasketIds,
            observation: this.existingCharge.observation || '',
            eventTime: this.adjustTimeZone3(new Date())
          };

          const res = await updateFurnaceChargeBaskets(payload, this.currentSiteId);
          if (res.data && (res.data.success || res.data.data)) {
            const msg = this.selectedGroups.length > 0
              ? `Carga do ${this.furnaceName} atualizada com sucesso!`
              : `Todos os cestos foram removidos. ${this.furnaceName} agora está Disponível!`;
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Carga Atualizada',
                icon: 'CheckIcon',
                text: msg,
                variant: 'success'
              }
            });
            this.goBack();
          } else {
            throw new Error(res.data?.message || 'Erro ao atualizar carga');
          }
        } else {
          const payload = {
            furnaceId: this.furnaceId,
            furnaceName: this.furnaceName,
            basketIds: allBasketIds,
            initialStatus: 'Em operação',
            observation: '',
            eventTime: this.adjustTimeZone3(new Date())
          };

          const res = await createFurnaceCharge(payload, this.currentSiteId);
          if (res.data && (res.data.success || res.data.data)) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Apontamento Realizado',
                icon: 'CheckIcon',
                text: `Fornada montada com sucesso no ${this.furnaceName}!`,
                variant: 'success'
              }
            });
            this.goBack();
          } else {
            throw new Error(res.data?.message || 'Erro ao registrar fornada');
          }
        }
      } catch (err) {
        console.error('Erro ao salvar fornada:', err);
        const msg = err.response?.data?.message || err.message || 'Erro ao registrar apontamento.';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            icon: 'AlertTriangleIcon',
            text: msg,
            variant: 'danger'
          }
        });
      } finally {
        this.saving = false;
      }
    },
    adjustTimeZone3(inputDate) {
      let date = inputDate || new Date();
      if (!(date instanceof Date)) {
        date = new Date(date);
      }
      const offset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - offset).toISOString().slice(0, -1);
    },
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(Number(val))) return '0';
      return Number(val).toLocaleString('pt-BR');
    },
    formatBarSizeNumber(item) {
      if (!item) return '-';
      let size = Number(item.barSizeMm);
      if (isNaN(size) || size <= 0) return '-';
      if (size <= 50) size *= 1000;
      return Math.round(size).toLocaleString('pt-BR');
    },
    formatBarSize(item) {
      const num = this.formatBarSizeNumber(item);
      if (num === '-') return '-';
      return `${num} mm`;
    },
    goBack() {
      const prefix = this.$route.params.prefix || 'sfc';
      this.$router.push({ name: 'operator-extrusion-furnaces', params: { prefix } });
    }
  }
};
</script>

<style lang="scss">
.app-content-operator:has(#furnace-assembly-page-container) {
  padding: 0 !important;
}

#furnace-assembly-page-container {
  min-height: calc(100vh - 110px);
  background-color: #fbfaf9;
  color: #332f2e;

  /* Top Tab Bar */
  .assembly-top-header {
    background: #fff;
    border-bottom: 1px solid #ebe6e2;

    .tabs-bar {
      display: flex;
      padding-left: 28px;

      .tab-item {
        padding: 16px 28px;
        font-family: 'Raleway', sans-serif;
        font-size: 16px;
        font-weight: 700;
        color: #332f2e;
        cursor: pointer;
        position: relative;

        &.active {
          color: #332f2e;
          border-top: 3px solid #e86b24;
          border-left: 1px solid #ebe6e2;
          border-right: 1px solid #ebe6e2;
          background: #fff;
          margin-bottom: -1px;
        }
      }
    }
  }

  /* Sub-Header Info Bar */
  .assembly-sub-header {
    background: #ffffff;
    border-bottom: 1px solid #ebe6e2;
    padding: 12px 32px;

    .btn-back-link {
      background: transparent;
      border: none;
      padding: 0;
      color: #4c4541;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      transition: color 0.2s;

      &:hover {
        color: #e86b24;
      }
    }

    .sub-header-center {
      .charge-code-title {
        font-family: 'Raleway', sans-serif;
        font-size: 18px;
        font-weight: 700;
        color: #332f2e;
      }

      .assembly-status-text {
        font-size: 12px;
        font-weight: 600;
        color: #28c76f;
      }
    }

    .cestos-counter-box {
      .cestos-label {
        font-size: 10px;
        font-weight: 700;
        color: #8b939e;
        letter-spacing: 0.5px;
        display: block;
      }

      .cestos-value {
        font-family: 'Raleway', sans-serif;
        font-size: 16px;
        font-weight: 800;
        color: #332f2e;
      }
    }
  }

  .operator-body {
    padding: 24px 32px;
  }

  /* Assembly Panels Layout */
  .assembly-panels-wrapper {
    display: flex;
    align-items: stretch;
    gap: 16px;

    @media (max-width: 991px) {
      flex-direction: column;
    }

    .assembly-panel-box {
      flex: 1;
      background: #ffffff;
      border: 1px solid #e6e4e2;
      border-radius: 16px;
      padding: 20px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);

      .panel-header-row {
        .panel-box-title {
          font-family: 'Raleway', sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: #332f2e;
          letter-spacing: 0.5px;
        }

        .panel-box-counter {
          font-size: 12px;
          color: #8b939e;
          font-weight: 500;
        }
      }

      .search-box-panel {
        .filters-row {
          display: flex;
          align-items: center;
          gap: 8px;

          @media (max-width: 1250px) {
            flex-wrap: wrap;
          }

          .filter-cesto {
            flex: 1 1 100px;
            min-width: 90px;
          }

          .filter-op {
            flex: 1 1 100px;
            min-width: 90px;
          }

          .filter-perfil {
            flex: 1.8 1 150px;
            min-width: 130px;
          }

          .btn-clear-all-filters {
            height: 38px;
            padding: 0 12px;
            background: #fff2e8;
            border: 1px solid #ffbb96;
            border-radius: 8px;
            color: #d4380d;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            white-space: nowrap;
            transition: all 0.2s ease;
            flex-shrink: 0;

            &:hover {
              background: #ffd8bf;
              border-color: #ff7a45;
              color: #a82400;
            }

            &:active {
              transform: translateY(1px);
            }
          }
        }

        .search-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;

          .search-icon {
            position: absolute;
            left: 10px;
            color: #8b939e;
            pointer-events: none;
          }

          .search-input-field {
            width: 100%;
            height: 38px;
            padding: 0 28px 0 30px;
            border: 1px solid #cfc4be;
            border-radius: 8px;
            font-size: 13px;
            color: #332f2e;
            background-color: #faf8f7;
            outline: none;
            transition: all 0.2s ease;

            &:focus {
              background-color: #ffffff;
              border-color: #e86b24;
              box-shadow: 0 0 0 3px rgba(232, 107, 36, 0.12);
            }

            &::placeholder {
              color: #a09893;
              font-size: 12px;
            }
          }

          .clear-search-btn {
            position: absolute;
            right: 6px;
            background: none;
            border: none;
            color: #8b939e;
            cursor: pointer;
            padding: 4px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition: color 0.15s, background-color 0.15s;

            &:hover {
              color: #e86b24;
              background-color: rgba(232, 107, 36, 0.08);
            }
          }
        }
      }

      .panel-table-container {
        border-radius: 8px;
        overflow-x: auto;
        overflow-y: hidden;
        border: 1px solid #ebe6e2;

        .panel-table-head {
          display: flex;
          align-items: center;
          padding: 10px 16px;
          min-width: 540px;
          color: #ffffff;
          font-size: 12px;
          font-weight: 700;

          &.table-head-navy {
            background-color: #243647;
          }

          &.table-head-orange {
            background-color: #e86b24;
          }
        }

        .col-id {
          width: 80px;
          min-width: 80px;
          flex-shrink: 0;
          text-align: left;
          padding-left: 2px;
          padding-right: 8px;

          .basket-code {
            font-size: 13px;
            color: #243647;
            white-space: nowrap;
          }

          .multi-op-badge {
            font-size: 10px;
            padding: 1px 4px;
            margin-top: 2px;
            display: inline-block;
            line-height: 1.2;
            border-radius: 4px;
          }
        }

        .col-details {
          flex: 1;
          min-width: 0;
          padding: 0 4px;
          font-size: 12px;

          &.col-details-head {
            display: flex;
            align-items: center;
            gap: 8px;

            .cell-op {
              width: 68px;
              min-width: 68px;
              flex-shrink: 0;
            }

            .cell-mat {
              flex: 1;
              min-width: 110px;
            }

            .cell-temper {
              width: 58px;
              min-width: 58px;
              flex-shrink: 0;
              text-align: center;
            }

            .cell-qty {
              width: 82px;
              min-width: 82px;
              flex-shrink: 0;
              text-align: center;
            }

            .cell-size {
              width: 88px;
              min-width: 88px;
              flex-shrink: 0;
              text-align: center;
            }
          }

          .details-line {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            padding: 3px 0;

            &.border-top-dashed {
              border-top: 1px dashed #e8e4e0;
              margin-top: 4px;
              padding-top: 4px;
            }

            .cell-op {
              width: 68px;
              min-width: 68px;
              flex-shrink: 0;
              white-space: nowrap;
            }

            .cell-mat {
              flex: 1;
              min-width: 110px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .cell-temper {
              width: 58px;
              min-width: 58px;
              flex-shrink: 0;
              text-align: center;
            }

            .cell-qty {
              width: 82px;
              min-width: 82px;
              flex-shrink: 0;
              text-align: center;
              white-space: nowrap;
              font-variant-numeric: tabular-nums;

              .qty-val {
                font-weight: 700;
                color: #243647;
                font-size: 12.5px;
              }

              .unit-text {
                font-size: 10.5px;
                color: #8b939e;
                font-weight: 500;
                margin-left: 2px;
              }
            }

            .cell-size {
              width: 88px;
              min-width: 88px;
              flex-shrink: 0;
              text-align: center;
              white-space: nowrap;
              font-variant-numeric: tabular-nums;

              .size-val {
                font-weight: 600;
                color: #243647;
                font-size: 12px;
              }

              .unit-text {
                font-size: 10.5px;
                color: #8b939e;
                font-weight: 500;
                margin-left: 2px;
              }
            }
          }

          .multi-op-total-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 4px;
            padding-top: 4px;
            border-top: 1px dashed #e2ded9;

            .cell-op {
              width: 68px;
              min-width: 68px;
              flex-shrink: 0;
            }

            .cell-mat {
              flex: 1;
              min-width: 110px;
              text-align: right;

              .total-label {
                font-size: 11px;
                font-weight: 600;
                color: #7367f0;
                text-transform: uppercase;
                letter-spacing: 0.3px;
              }
            }

            .cell-temper {
              width: 58px;
              min-width: 58px;
              flex-shrink: 0;
            }

            .cell-qty {
              width: 82px;
              min-width: 82px;
              flex-shrink: 0;
              text-align: center;
              white-space: nowrap;
              font-variant-numeric: tabular-nums;

              .total-qty {
                font-size: 12px;
                font-weight: 700;
                color: #7367f0;
              }

              .unit-text {
                font-size: 10.5px;
                color: #7367f0;
                font-weight: 600;
                margin-left: 2px;
              }
            }

            .cell-size {
              width: 88px;
              min-width: 88px;
              flex-shrink: 0;
            }
          }
        }

        .col-action {
          width: 36px;
          min-width: 36px;
          flex-shrink: 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .panel-table-body {
          max-height: 400px;
          min-height: 320px;
          overflow-y: auto;
          background: #ffffff;

          .panel-table-row {
            display: flex;
            align-items: center;
            padding: 10px 16px;
            min-width: 540px;
            border-bottom: 1px solid #f0eeeb;
            transition: background 0.15s ease;
            cursor: pointer;

            &:last-child {
              border-bottom: none;
            }

            &:hover {
              background: #fdfbf7;
            }

            &.row-staged {
              background: #fffdf0;
            }
          }
        }
      }
    }

    /* Central Transfer Action */
    .assembly-transfer-column {
      width: 90px;
      padding: 0 8px;

      @media (max-width: 991px) {
        width: 100%;
        margin: 12px 0;
      }

      .transfer-label {
        font-size: 11px;
        color: #8b939e;
        text-align: center;
        line-height: 1.2;
      }

      .btn-transfer-circle {
        background: #fff3d6;
        border: none;
        width: 42px;
        height: 42px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #e86b24;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 2px 6px rgba(232, 107, 36, 0.15);

        &:hover:not(:disabled) {
          background: #ffe5a3;
          transform: scale(1.08);
        }

        &:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          box-shadow: none;
        }
      }
    }
  }

  /* Custom Checkbox */
  .custom-checkbox-box {
    width: 20px;
    height: 20px;
    border: 2px solid #d8d6de;
    border-radius: 4px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    transition: all 0.2s ease;

    &.is-checked {
      background-color: #e86b24;
      border-color: #e86b24;
    }
  }

  /* Remove Button in Selected Table */
  .btn-remove-basket {
    background: #ffffff;
    border: 1px solid #ebe6e2;
    border-radius: 6px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8b939e;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #ea5455;
      border-color: #ea5455;
      color: #ffffff;
    }
  }

  /* Footer Confirm Button */
  .assembly-footer-action {
    .btn-confirm-appointment {
      background-color: #974900;
      color: #ffffff;
      font-family: 'Raleway', sans-serif;
      font-size: 15px;
      font-weight: 700;
      padding: 14px 24px;
      border-radius: 10px;
      border: none;
      width: 100%;
      cursor: pointer;
      transition: background 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(151, 73, 0, 0.18);

      &:hover:not(:disabled) {
        background-color: #7d3c00;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
